import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

export interface PlannerLogRange {
    start: Date;
    end: Date;
}

type TimeField = 'start' | 'end';

// The "Log as time" form's time row — same look and behaviour as the Timer
// calendar's own event-modal time row (timer-tracker__modal-timerow in
// timer-tracker.component): segmented HH : MM start/end pickers, a date link
// beside each that opens the month date picker, a "+Nd" badge when the
// entry runs past midnight, and a click-to-edit duration (H : MM : SS, as
// the Timer's; the start and end themselves are whole minutes).
// Duplicated (behaviour ported from timer-tracker's onSeg*/onDurSeg*/
// date-picker methods) rather than shared, same Timer-isolation reasoning as
// the rest of the Planner — the Timer's code is never touched by this.
//
// Works on two Dates (start, end) and emits a fresh pair on every change;
// the parent owns them. Simplified from the Timer only where the Timer has
// live-entry or seconds-preservation concerns a planned block doesn't.
@Component({
    selector: 'planner-log-timerow',
    templateUrl: './planner-log-timerow.component.html',
    styleUrls: ['./planner-log-timerow.component.scss'],
})
export class PlannerLogTimerowComponent implements OnChanges {
    @Input() start: Date;
    @Input() end: Date;
    @Output() rangeChange = new EventEmitter<PlannerLogRange>();

    // Same limits as the Timer modal.
    private static readonly DURATION_HOURS_MAX = 99;
    private static readonly MAX_DURATION_MS = 99 * 60 * 60 * 1000;
    private static readonly WARN_DURATION_MS = 48 * 60 * 60 * 1000;
    // Start/end are HH : MM; the duration is H : MM : SS, as the Timer
    // modal's (its seconds move the end by seconds).
    private static readonly SEG_NAV_ORDER = ['start-h', 'start-m', 'end-h', 'end-m', 'dur-h', 'dur-m', 'dur-s'];

    private _start = new Date();
    private _end = new Date();

    // Start/end HH : MM segments (Timer: activeTimeSeg / timeBuf / …)
    private activeTimeSeg: string | null = null;
    private timeBuf = '';
    private timeBufDirty = false;
    private timeSuppressAdvance = false;

    // Click-to-edit duration (Timer: editingDuration / durBuf / …)
    editingDuration = false;
    private durHours = 0;
    private durMinutes = 0;
    private durSeconds = 0;
    private activeDurSeg: string | null = null;
    private durBuf = '';
    private durBufDirty = false;
    private durSuppressAdvance = false;

    // Date picker (Timer: showDatePicker / datePickerTarget / …)
    showDatePicker = false;
    datePickerTarget: TimeField = 'start';
    datePickerUpward = false;
    pickerMonth = new Date();

    constructor(private _elRef: ElementRef<HTMLElement>, private _changeRef: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['start'] && this.start) this._start = this.toMinute(this.start);
        if (changes['end'] && this.end) this._end = this.toMinute(this.end);
    }

    // Whole minutes only (see SEG_NAV_ORDER).
    private toMinute(date: Date): Date {
        const d = new Date(date);
        d.setSeconds(0, 0);
        return d;
    }

    get startValue(): Date { return this._start; }
    get endValue(): Date { return this._end; }

    /** Whole days between the start and end dates — drives the "+Nd" badge. */
    get endDayOffset(): number {
        const s = new Date(this._start); s.setHours(0, 0, 0, 0);
        const e = new Date(this._end); e.setHours(0, 0, 0, 0);
        return Math.round((e.getTime() - s.getTime()) / 86400000);
    }

    /** "1:30:00" — hours, minutes and seconds, as the Timer shows it. */
    get durationText(): string {
        const diff = this._end.getTime() - this._start.getTime();
        if (diff <= 0) return '0:00:00';
        const total = Math.floor(diff / 1000);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }

    // Advisory only, same as the Timer modal's own warning.
    get isOverWarnDuration(): boolean {
        return this._end.getTime() - this._start.getTime() > PlannerLogTimerowComponent.WARN_DURATION_MS;
    }

    /** Called by the Planner's Escape handling first: closes the date
     *  picker if it's open. Returns true if it handled the key. */
    handleEscape(): boolean {
        if (this.showDatePicker) {
            this.showDatePicker = false;
            return true;
        }
        return false;
    }

    private emit(): void {
        this.rangeChange.emit({ start: new Date(this._start), end: new Date(this._end) });
    }

    /** End at or before the start means the entry runs past midnight — move
     *  the end forward a day at a time (at most twice), same as the Timer's
     *  normalizeEditEndDateForMultiDay. */
    private normalizeEndForMultiDay(): void {
        let safety = 2;
        while (this._end.getTime() <= this._start.getTime() && safety-- > 0) {
            this._end.setDate(this._end.getDate() + 1);
        }
    }

    // ─── Start / end HH : MM segments ────────────────────────────────────────

    getSegDisplay(segId: string): string {
        const d = segId.startsWith('start') ? this._start : this._end;
        const editing = this.activeTimeSeg === segId && this.timeBufDirty;
        if (segId.endsWith('-h')) return editing ? this.timeBuf : String(d.getHours()).padStart(2, '0');
        return editing ? this.timeBuf : String(d.getMinutes()).padStart(2, '0');
    }

    onSegFocus(segId: string): void {
        this.activeTimeSeg = segId;
        this.timeBuf = '';
        this.timeBufDirty = false;
        this.timeSuppressAdvance = false;
    }

    onSegBlur(segId: string): void {
        if (this.timeBuf) {
            const isHours = segId.endsWith('-h');
            const val = Math.max(0, Math.min(isHours ? 23 : 59, parseInt(this.timeBuf, 10)));
            if (!isNaN(val)) this.setTimePart(this.fieldOf(segId), isHours ? 'h' : 'm', val);
        }
        this.timeBuf = '';
        this.timeBufDirty = false;
        this.activeTimeSeg = null;
    }

    onSegKeydown(e: KeyboardEvent, segId: string): void {
        if (e.key === 'Tab') return;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            this.handleSegArrow(e, segId);
            return;
        }
        // Let Escape through to the Planner's own handling (closes the popover).
        if (e.key === 'Escape') return;

        e.preventDefault();
        const isHours = segId.endsWith('-h');
        const input = e.target as HTMLInputElement;
        const caret = input.selectionStart ?? (this.timeBufDirty ? this.timeBuf.length : 2);

        if (/^\d$/.test(e.key)) {
            this.insertSegDigit(segId, e.key, caret);
            return;
        }
        if (e.key === 'ArrowUp')   { this.timeBuf = ''; this.timeBufDirty = false; this.adjustSeg(segId,  1); return; }
        if (e.key === 'ArrowDown') { this.timeBuf = ''; this.timeBufDirty = false; this.adjustSeg(segId, -1); return; }

        if (e.key === 'Backspace') {
            this.timeSuppressAdvance = true;
            if (!this.timeBufDirty) {
                const d = this.fieldOf(segId) === 'start' ? this._start : this._end;
                this.timeBuf = String(isHours ? d.getHours() : d.getMinutes()).padStart(2, '0');
                this.timeBufDirty = true;
            }
            const at = Math.min(caret, this.timeBuf.length);
            if (at > 0) {
                this.timeBuf = this.timeBuf.slice(0, at - 1) + this.timeBuf.slice(at);
                this.scheduleSegCaret(segId, at - 1);
            }
            return;
        }
        if (e.key === 'Delete') {
            this.timeSuppressAdvance = true;
            this.timeBuf = '';
            this.timeBufDirty = true;
            this.scheduleSegCaret(segId, 0);
        }
    }

    /** Fallback for mobile keyboards, whose keydown.key is often "Unidentified". */
    onSegInput(e: Event, segId: string): void {
        const input = e.target as HTMLInputElement;
        const ie = e as InputEvent;
        if (ie.inputType === 'insertText' && ie.data && /^\d$/.test(ie.data)) {
            const caret = Math.max(0, (input.selectionStart ?? this.timeBuf.length) - 1);
            this.insertSegDigit(segId, ie.data, caret);
        }
        input.value = this.getSegDisplay(segId);
    }

    private fieldOf(segId: string): TimeField {
        return segId.startsWith('start') ? 'start' : 'end';
    }

    private insertSegDigit(segId: string, digit: string, caret: number): void {
        const isHours = segId.endsWith('-h');
        if (!this.timeBufDirty) {
            this.timeBuf = '';
            this.timeBufDirty = true;
        }
        const at = this.timeBuf.length === 0 ? 0 : Math.min(caret, this.timeBuf.length);
        this.timeBuf = this.timeBuf.slice(0, at) + digit + this.timeBuf.slice(at);
        this.scheduleSegCaret(segId, at + 1);

        const partial = parseInt(this.timeBuf, 10);
        // Two digits in, or a first digit too big to be followed by another.
        const autoComplete = this.timeBuf.length >= 2 || (isHours ? partial > 2 : partial > 5);
        if (autoComplete) {
            const appendedAtEnd = at + 1 === this.timeBuf.length;
            this.setTimePart(this.fieldOf(segId), isHours ? 'h' : 'm', Math.max(0, Math.min(isHours ? 23 : 59, partial)));
            this.timeBuf = '';
            this.timeBufDirty = false;
            if (appendedAtEnd && !this.timeSuppressAdvance) this.advanceSeg(segId);
        }
    }

    private setTimePart(field: TimeField, part: 'h' | 'm', value: number): void {
        const d = new Date(field === 'start' ? this._start : this._end);
        if (part === 'h') d.setHours(value);
        else d.setMinutes(value);
        d.setSeconds(0, 0);
        if (field === 'start') this._start = d;
        else this._end = d;
        this.normalizeEndForMultiDay();
        this.emit();
    }

    private adjustSeg(segId: string, delta: number): void {
        const d = this.fieldOf(segId) === 'start' ? this._start : this._end;
        if (segId.endsWith('-h')) {
            let v = d.getHours() + delta; if (v > 23) v = 0; if (v < 0) v = 23;
            this.setTimePart(this.fieldOf(segId), 'h', v);
        } else {
            let v = d.getMinutes() + delta; if (v > 59) v = 0; if (v < 0) v = 59;
            this.setTimePart(this.fieldOf(segId), 'm', v);
        }
    }

    private advanceSeg(segId: string): void {
        const order = ['start-h', 'start-m', 'end-h', 'end-m'];
        const next = order[order.indexOf(segId) + 1];
        if (next) this.segEl(next)?.focus();
    }

    // ─── Click-to-edit duration (H : MM : SS) — changes move the end ─────────

    startEditingDuration(caretPos?: number): void {
        const total = Math.max(0, Math.floor((this._end.getTime() - this._start.getTime()) / 1000));
        this.durHours = Math.min(PlannerLogTimerowComponent.DURATION_HOURS_MAX, Math.floor(total / 3600));
        this.durMinutes = Math.floor((total % 3600) / 60);
        this.durSeconds = total % 60;
        this.activeDurSeg = null;
        this.durBuf = '';
        this.editingDuration = true;
        setTimeout(() => {
            const input = this.segEl('dur-h');
            input?.focus();
            if (caretPos != null) {
                try { input?.setSelectionRange(caretPos, caretPos); } catch { /* not focusable */ }
            } else {
                input?.select();
            }
        }, 0);
    }

    getDurSegDisplay(segId: string): string {
        const editing = this.activeDurSeg === segId && this.durBufDirty;
        if (segId === 'dur-h') return editing ? this.durBuf : String(this.durHours);
        if (segId === 'dur-s') return editing ? this.durBuf : String(this.durSeconds).padStart(2, '0');
        return editing ? this.durBuf : String(this.durMinutes).padStart(2, '0');
    }

    onDurSegFocus(segId: string): void {
        this.activeDurSeg = segId;
        this.durBuf = '';
        this.durBufDirty = false;
        this.durSuppressAdvance = false;
    }

    onDurSegBlur(segId: string): void {
        if (this.durBuf) this.commitDurBuf(segId);
        this.durBuf = '';
        this.durBufDirty = false;
        this.activeDurSeg = null;
        // Leave edit mode once focus has moved out of both segments.
        setTimeout(() => {
            const focused = document.activeElement as HTMLElement | null;
            const stillInGroup = !!focused && !!focused.getAttribute('data-seg-id')?.startsWith('dur-')
                && this._elRef.nativeElement.contains(focused);
            if (!stillInGroup && this.editingDuration) {
                this.editingDuration = false;
                // Changed from a timer, not a template event — the Actions
                // page hosting the Planner is OnPush, so ask for a re-check
                // (see PlannerComponent.openContextMenu).
                this._changeRef.markForCheck();
            }
        }, 0);
    }

    onDurSegKeydown(e: KeyboardEvent, segId: string): void {
        if (e.key === 'Tab') return;
        if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault();
            // Escape here only leaves duration editing — it shouldn't also
            // close the whole popover.
            e.stopPropagation();
            if (e.key === 'Enter' && this.durBuf) this.commitDurBuf(segId);
            this.durBuf = '';
            this.durBufDirty = false;
            this.activeDurSeg = null;
            this.editingDuration = false;
            (e.target as HTMLInputElement)?.blur();
            return;
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            this.handleSegArrow(e, segId);
            return;
        }

        e.preventDefault();
        const isHours = segId === 'dur-h';
        // Seconds are the last duration segment — nothing to advance to.
        const isLast = segId === 'dur-s';
        const input = e.target as HTMLInputElement;
        const caret = input.selectionStart ?? this.durBuf.length;

        if (/^\d$/.test(e.key)) {
            if (!this.durBufDirty) {
                this.durBuf = '';
                this.durBufDirty = true;
            }
            const at = this.durBuf.length === 0 ? 0 : Math.min(caret, this.durBuf.length);
            this.durBuf = this.durBuf.slice(0, at) + e.key + this.durBuf.slice(at);
            this.scheduleSegCaret(segId, at + 1);
            const partial = parseInt(this.durBuf, 10);
            const autoComplete = isHours ? this.durBuf.length >= 2 : this.durBuf.length >= 2 || partial > 5;
            if (autoComplete) {
                const appendedAtEnd = at + 1 === this.durBuf.length;
                this.commitDurBuf(segId);
                this.durBuf = '';
                this.durBufDirty = false;
                if (appendedAtEnd && !isLast && !this.durSuppressAdvance) this.advanceDurSeg(segId);
            }
            return;
        }
        if (e.key === 'ArrowUp')   { this.durBuf = ''; this.durBufDirty = false; this.adjustDurSeg(segId,  1); return; }
        if (e.key === 'ArrowDown') { this.durBuf = ''; this.durBufDirty = false; this.adjustDurSeg(segId, -1); return; }

        if (e.key === 'Backspace') {
            this.durSuppressAdvance = true;
            if (!this.durBufDirty) {
                const current = isHours ? this.durHours : segId === 'dur-s' ? this.durSeconds : this.durMinutes;
                this.durBuf = isHours ? String(current) : String(current).padStart(2, '0');
                this.durBufDirty = true;
            }
            const at = Math.min(caret, this.durBuf.length);
            if (at > 0) {
                this.durBuf = this.durBuf.slice(0, at - 1) + this.durBuf.slice(at);
                this.scheduleSegCaret(segId, at - 1);
            }
            return;
        }
        if (e.key === 'Delete') {
            this.durSuppressAdvance = true;
            this.durBuf = '';
            this.durBufDirty = true;
            this.scheduleSegCaret(segId, 0);
        }
    }

    onDurSegInput(e: Event, segId: string): void {
        (e.target as HTMLInputElement).value = this.getDurSegDisplay(segId);
    }

    private commitDurBuf(segId: string): void {
        const val = parseInt(this.durBuf, 10);
        if (isNaN(val)) return;
        if (segId === 'dur-h') this.setDurPart('h', Math.max(0, Math.min(PlannerLogTimerowComponent.DURATION_HOURS_MAX, val)));
        else this.setDurPart(segId === 'dur-s' ? 's' : 'm', Math.max(0, Math.min(59, val)));
    }

    private adjustDurSeg(segId: string, delta: number): void {
        if (segId === 'dur-h') {
            this.setDurPart('h', Math.max(0, Math.min(PlannerLogTimerowComponent.DURATION_HOURS_MAX, this.durHours + delta)));
        } else {
            const part = segId === 'dur-s' ? 's' : 'm';
            let v = (part === 's' ? this.durSeconds : this.durMinutes) + delta; if (v > 59) v = 0; if (v < 0) v = 59;
            this.setDurPart(part, v);
        }
    }

    private advanceDurSeg(segId: string): void {
        const order = ['dur-h', 'dur-m', 'dur-s'];
        const next = order[order.indexOf(segId) + 1];
        if (next) this.segEl(next)?.focus();
    }

    // New duration → new end (the start stays put). Floors at 1 second — a
    // zero-length entry isn't a real record (same as the Timer).
    private setDurPart(part: 'h' | 'm' | 's', value: number): void {
        if (part === 'h') this.durHours = value;
        else if (part === 'm') this.durMinutes = value;
        else this.durSeconds = value;
        if (this.durHours === 0 && this.durMinutes === 0 && this.durSeconds === 0) this.durSeconds = 1;
        const ms = ((this.durHours * 60 + this.durMinutes) * 60 + this.durSeconds) * 1000;
        this._end = new Date(this._start.getTime() + ms);
        this.emit();
    }

    // ─── Segment navigation (shared by time + duration segments) ─────────────

    private segEl(segId: string): HTMLInputElement | null {
        return this._elRef.nativeElement.querySelector(`[data-seg-id="${segId}"]`) as HTMLInputElement | null;
    }

    // Keeps the caret where it was after Angular re-renders a segment's value.
    private scheduleSegCaret(segId: string, pos: number): void {
        setTimeout(() => {
            const el = this.segEl(segId);
            if (el && document.activeElement === el) {
                try { el.setSelectionRange(pos, pos); } catch { /* not focusable */ }
            }
        }, 0);
    }

    // Left/Right move within a segment, crossing to the next one only at its edge.
    private handleSegArrow(e: KeyboardEvent, segId: string): void {
        const input = e.target as HTMLInputElement;
        const len = input.value.length;
        const pos = input.selectionStart ?? len;
        const min = segId.endsWith('-h') ? 0 : 1;
        if (e.key === 'ArrowRight') {
            if (pos < len) return;
            e.preventDefault();
            this.crossSeg(segId, 1);
        } else {
            if (pos > min) return;
            e.preventDefault();
            this.crossSeg(segId, -1);
        }
    }

    private crossSeg(segId: string, delta: 1 | -1): void {
        const order = PlannerLogTimerowComponent.SEG_NAV_ORDER;
        let idx = order.indexOf(segId);
        if (idx < 0) return;
        while (true) {
            idx += delta;
            if (idx < 0 || idx >= order.length) return;
            const target = order[idx];
            const el = this.segEl(target);
            if (el && !el.disabled) {
                el.focus();
                const min = target.endsWith('-h') ? 0 : 1;
                const caret = delta === 1 ? min : el.value.length;
                try { el.setSelectionRange(caret, caret); } catch { /* not focusable */ }
                return;
            }
            if (target.startsWith('dur-') && !this.editingDuration) {
                this.startEditingDuration(0);
                return;
            }
        }
    }

    // ─── Date picker ─────────────────────────────────────────────────────────

    toggleDatePicker(target: TimeField): void {
        if (this.showDatePicker && this.datePickerTarget === target) {
            this.showDatePicker = false;
            return;
        }
        this.datePickerTarget = target;
        this.showDatePicker = true;
        this.pickerMonth = new Date(target === 'end' ? this._end : this._start);
        // Open upward when there isn't room below (picker ≈ 210px tall).
        const rect = this._elRef.nativeElement.getBoundingClientRect();
        this.datePickerUpward = rect.bottom + 210 > document.documentElement.clientHeight - 20;
    }

    // Closes on any press outside the picker and its two date links.
    @HostListener('document:mousedown', ['$event'])
    onDocumentMousedown(e: MouseEvent): void {
        if (!this.showDatePicker) return;
        const target = e.target as HTMLElement | null;
        if (target?.closest('.PlannerLogRow-datePicker') || target?.closest('.PlannerLogRow-dateLink')) return;
        this.showDatePicker = false;
    }

    pickerPrevMonth(): void {
        this.pickerMonth = new Date(this.pickerMonth.getFullYear(), this.pickerMonth.getMonth() - 1, 1);
    }

    pickerNextMonth(): void {
        this.pickerMonth = new Date(this.pickerMonth.getFullYear(), this.pickerMonth.getMonth() + 1, 1);
    }

    get pickerMonthLabel(): string {
        return this.pickerMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }

    /** Mon-first 7-column grid of Date | null entries. */
    get pickerDays(): (Date | null)[] {
        const year = this.pickerMonth.getFullYear();
        const month = this.pickerMonth.getMonth();
        const lastDay = new Date(year, month + 1, 0);
        let startDow = new Date(year, month, 1).getDay();
        startDow = startDow === 0 ? 6 : startDow - 1;
        const days: (Date | null)[] = [];
        for (let i = 0; i < startDow; i++) days.push(null);
        for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
        while (days.length % 7 !== 0) days.push(null);
        return days;
    }

    isToday(date: Date | null): boolean {
        if (!date) return false;
        return date.toDateString() === new Date().toDateString();
    }

    isSelectedDate(date: Date | null): boolean {
        if (!date) return false;
        const ref = this.datePickerTarget === 'end' ? this._end : this._start;
        return date.toDateString() === ref.toDateString();
    }

    /** End-date mode: days before the start, or that would make the entry
     *  longer than 99h, can't be picked — same rules as the Timer. */
    isDateDisabled(date: Date | null): boolean {
        if (!date || this.datePickerTarget === 'start') return false;
        const d = new Date(date); d.setHours(0, 0, 0, 0);
        const s = new Date(this._start); s.setHours(0, 0, 0, 0);
        if (d.getTime() < s.getTime()) return true;
        const end = new Date(date);
        end.setHours(this._end.getHours(), this._end.getMinutes(), 0, 0);
        return end.getTime() - this._start.getTime() > PlannerLogTimerowComponent.MAX_DURATION_MS;
    }

    onPickerDayClick(day: Date | null): void {
        if (!day || this.isDateDisabled(day)) return;
        if (this.datePickerTarget === 'end') {
            const end = new Date(day);
            end.setHours(this._end.getHours(), this._end.getMinutes(), 0, 0);
            this._end = end;
        } else {
            // Moving the start keeps the entry's whole-day span (the end moves
            // with it), same as the Timer.
            const span = Math.max(0, this.endDayOffset);
            const start = new Date(day);
            start.setHours(this._start.getHours(), this._start.getMinutes(), 0, 0);
            const end = new Date(day);
            end.setDate(end.getDate() + span);
            end.setHours(this._end.getHours(), this._end.getMinutes(), 0, 0);
            this._start = start;
            this._end = end;
        }
        // Never leave the end at or before the start.
        this.normalizeEndForMultiDay();
        this.pickerMonth = new Date(day);
        this.showDatePicker = false;
        this.emit();
    }

    trackByIndex(index: number): number { return index; }
}
